import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import {
  UserIcon,
  MapPinIcon,
  BriefcaseIcon,
  FileSignatureIcon,
  EditIcon,
  PercentIcon,
  CalendarIcon,
  MailIcon,
  PhoneIcon,
  BuildingIcon,
  MapIcon,
  TrashIcon,
  FileTextIcon
} from 'lucide-react'
import { TypeCargo } from '@/lib/enums'
import ActivateUserAler from './activiate-user-alert'
import { memo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import { formatDate, formatNome } from '@/utils/format'
import { useCurrentUser } from '@/features/auth/state/auth'
import { ROUTE_BUILDERS } from '@/config/routes'
import { useDeleteUser } from '../services/useDeleteUser'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/lib/hooks/use-toast'
import { AxiosError } from 'axios'

import ViewSignaturePage from '@/features/signatures/components/ViewSignaturePage'
import { useUpdateUserComissao } from '../services/useUpdateUserComissao'
import { useUpdateUserCargo } from '../services/useUpdateUserCargo'
import { numeroPorExtenso } from '@/utils/numeroPorExtenso'

import { DEFAULT_USER_PARTNERSHIP_CONTRACT } from '@/types/contrato-servico'
import { getStatusColor } from '@/features/products/utils'
import { useCreateAgreementUser } from '@/features/agreement/services/useCreateAgreementUser'
import { User } from '@/types/user'

type InfoUsuarioProps = {
  user: User
  handleDeleteUser: () => void
}

export default memo(function InfoUsuario({ user, handleDeleteUser }: InfoUsuarioProps) {
  const currentUser = useCurrentUser()
  const { toast } = useToast()
  const navigate = useNavigate()
  const mutation = useDeleteUser()
  const queryClient = useQueryClient()
  const [isEditingCargo, setIsEditingCargo] = useState(false)
  const [isEditingComissao, setIsEditingComissao] = useState(false)

  const [cargoForm, setCargoForm] = useState<string>(user.typeCargo ?? '')
  const [comissaoForm, setComissaoForm] = useState<string>(user.comissao_user != null ? String(user.comissao_user) : '')
  const handleClick = () => {
    if (user.id) {
      navigate(ROUTE_BUILDERS.minuteUser(user.id))
    }
  }

  const handleClickDeleteUser = () => {
    if (!user.id) return
    mutation.mutate(user.id, {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ['gerente']
        })
        queryClient.refetchQueries({
          queryKey: ['user', 'tenant']
        })
        handleDeleteUser()
      },
      onError: error => {
        console.error('Erro ao excluir usuário:', error)
        const message = error instanceof AxiosError ? error.response?.data.message : 'Erro ao tentar deletar usuário.'
        toast({
          title: 'Erro',
          description: message,
          variant: 'destructive'
        })
      }
    })
  }
  const { mutate: createAgreement } = useCreateAgreementUser()
  const handleEnviarMinuta = async (email: string) => {
    createAgreement({ contracts: [DEFAULT_USER_PARTNERSHIP_CONTRACT], email })
  }
  // no topo do componente (depois dos useState atuais)
  const { mutate: updateUserCargo } = useUpdateUserCargo({
    onSuccess: () => setIsEditingCargo(false)
  })

  const { mutate: updateUserComissao } = useUpdateUserComissao({
    onSuccess: () => {
      setIsEditingComissao(false)
      handleEnviarMinuta(user.email || '')
    }
  })

  const handleSaveCargo = () => {
    if (!user.id) return
    if (!cargoForm) {
      toast({ title: 'Atenção', description: 'Selecione um cargo.', variant: 'destructive' })
      return
    }

    updateUserCargo({ id: user.id, typeCargo: cargoForm })
  }

  const handleCancelCargo = () => {
    setCargoForm(user.typeCargo ?? '')
    setIsEditingCargo(false)
  }
  // salvar comissão
  const handleSaveComissao = () => {
    if (!user.id) return
    const valorNumerico = parseInt(comissaoForm, 10)

    if (Number.isNaN(valorNumerico)) {
      toast({ title: 'Atenção', description: 'Informe um número válido.', variant: 'destructive' })
      return
    }
    if (valorNumerico < 0 || valorNumerico > 100) {
      toast({ title: 'Atenção', description: 'A comissão deve estar entre 0 e 100.', variant: 'destructive' })
      return
    }

    const valorExtenso = numeroPorExtenso(valorNumerico)

    updateUserComissao({
      id: user.id,
      comissao_user: valorNumerico,
      comissao_user_escrita: valorExtenso
    })
  }

  // cancelar comissão
  const handleCancelComissao = () => {
    setComissaoForm(user.comissao_user != null ? String(user.comissao_user) : '')
    setIsEditingComissao(false)
  }

  const createdDateDisplay =
    user.createdDate && user.createdDate !== 'N/A' ? formatDate(user.createdDate) : 'Não informado'
  console.log(`info: ${JSON.stringify(user)}`)
  const isCNPJ = user.cnpj ? true : false
  const canDelete = currentUser?.id !== user.id

  return (
    <div className="w-full space-y-6">
      {/* Header com informações principais */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-primary to-primary-hover flex items-center justify-center text-foreground-secondary text-xl font-bold">
                {formatNome(user.name || '')
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .slice(0, 2)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatNome(user.name || '')}</h1>
                <p className="text-gray-600 dark:text-gray-400 flex items-center gap-2">
                  <MailIcon className="h-4 w-4" />
                  {user.email}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className={getStatusColor(user?.status)}>
                    {user?.status ?? '—'}
                  </Badge>
                  <Badge variant="outline">{user?.typeUser ?? '—'}</Badge>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClick} className="gap-2 bg-transparent">
                <FileTextIcon className="h-4 w-4" />
                Ver Minuta
              </Button>
              {currentUser?.typeCargo === 'CEO' && canDelete && (
                <Button
                  variant="outline"
                  onClick={handleClickDeleteUser}
                  disabled={mutation.isPending}
                  className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 bg-transparent"
                >
                  <TrashIcon className="h-4 w-4" />
                  {mutation.isPending ? 'Excluindo...' : 'Excluir'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <ActivateUserAler user={user} />

      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:grid-cols-4 h-auto p-1">
          <TabsTrigger value="personal" className="gap-2 py-3">
            <UserIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Pessoal</span>
          </TabsTrigger>
          <TabsTrigger value="address" className="gap-2 py-3">
            <MapPinIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Endereço</span>
          </TabsTrigger>
          <TabsTrigger value="signature" className="gap-2 py-3">
            <FileSignatureIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Assinatura</span>
          </TabsTrigger>
          {isCNPJ && (
            <TabsTrigger value="accounting" className="gap-2 py-3">
              <BriefcaseIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Empresa</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="personal" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="h-5 w-5" />
                Informações Pessoais
              </CardTitle>
              <CardDescription>Dados pessoais cadastrados no sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Nome Completo</label>
                  <p className="text-gray-900 dark:text-gray-100 font-medium">{formatNome(user.name || '')}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CPF</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.cpf || 'Não informado'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <MailIcon className="h-4 w-4" />
                    Email
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">{user.email}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4" />
                    WhatsApp
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">{user.whatsApp || 'Não informado'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    Data de Cadastro
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">{createdDateDisplay}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {currentUser?.typeCargo === `CEO` && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BriefcaseIcon className="h-5 w-5" />
                  Cargo e Comissão
                </CardTitle>
                <CardDescription>Informações profissionais e comissão do usuário</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cargo</label>

                    {!isEditingCargo ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditingCargo(true)}
                        className="gap-2 h-8 px-3"
                      >
                        <EditIcon className="h-3 w-3" />
                        Editar
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleSaveCargo} className="h-8 px-3">
                          Salvar
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancelCargo} className="h-8 px-3">
                          Cancelar
                        </Button>
                      </div>
                    )}
                  </div>

                  {!isEditingCargo ? (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <p className="text-gray-900 dark:text-gray-100 font-medium">{formatNome(user.typeCargo)}</p>
                    </div>
                  ) : (
                    // se você já usa shadcn Select, pode trocar por ele
                    <select
                      className="w-full rounded-md border px-3 py-2 bg-white dark:bg-gray-900"
                      value={cargoForm}
                      onChange={e => setCargoForm(e.target.value)}
                    >
                      {/* ajuste as opções conforme seu enum TypeCargo */}
                      <option value="">Selecione…</option>
                      <option value={TypeCargo.CEO}>CEO</option>
                      <option value={TypeCargo.PARCEIRO}>Parceiro Comercial</option>
                      <option value={TypeCargo.GERENTE}>Parceiro Gestor</option>
                    </select>
                  )}
                </div>

                {user.comissao_user != null && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                        <PercentIcon className="h-4 w-4" />
                        Comissão
                      </label>

                      {!isEditingComissao ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsEditingComissao(true)}
                          className="gap-2 h-8 px-3"
                        >
                          <EditIcon className="h-3 w-3" />
                          Alterar
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleSaveComissao} className="h-8 px-3">
                            Salvar
                          </Button>
                          <Button size="sm" variant="outline" onClick={handleCancelComissao} className="h-8 px-3">
                            Cancelar
                          </Button>
                        </div>
                      )}
                    </div>

                    {!isEditingComissao ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                        <p className="text-green-800 dark:text-green-200 font-bold text-lg">{user.comissao_user}%</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1} // só números inteiros
                          className="w-40 rounded-md border px-3 py-2 bg-white dark:bg-gray-900"
                          value={comissaoForm}
                          onChange={e => setComissaoForm(e.target.value)}
                          placeholder="Ex.: 12"
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="address" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapIcon className="h-5 w-5" />
                Endereço
              </CardTitle>
              <CardDescription>Endereço cadastrado no sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CEP</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.cep || 'Não informado'}</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Logradouro</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.logradouro || 'Não informado'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Número</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.numero || 'Não informado'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Complemento</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.complemento || 'Não informado'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Bairro</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.bairro || 'Não informado'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Município</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.municipio || 'Não informado'}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Estado</label>
                  <p className="text-gray-900 dark:text-gray-100">{user.estado || 'Não informado'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isCNPJ && (
          <TabsContent value="accounting" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BuildingIcon className="h-5 w-5" />
                  Informações de Contabilidade
                </CardTitle>
                <CardDescription>Dados específicos para usuários de contabilidade</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4" />
                      Telefone
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">{user.tel || 'Não informado'}</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">CNPJ</label>
                    <p className="text-gray-900 dark:text-gray-100">{user.cnpj || 'Não informado'}</p>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Razão Social</label>
                    <p className="text-gray-900 dark:text-gray-100">{user.razao_social || 'Não informado'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="signature" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSignatureIcon className="h-5 w-5" />
                Assinatura Digital
              </CardTitle>
              <CardDescription>Verificação e gerenciamento de assinatura digital</CardDescription>
            </CardHeader>
            <CardContent>
              <ViewSignaturePage id={user.id || ''} role={`user`} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
})
